/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useRef, useState } from "react";
import useSystemConfig from "../../../hooks/useSystemConfig";
import parseConfirmedAppointment, {
  ConfirmedAppointment,
  SupabaseConfirmedAppointment,
  supabaseConfirmedAppointmentRequest,
} from "./parseConfirmedAppointment";
import { useSupabaseClient } from "../../../common/supabaseClient";

const refetchIntervalMs = 5000;

const useConfirmedAppointment = (id?: string) => {
  const [data, setData] = useState<ConfirmedAppointment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const supabase = useSupabaseClient();

  const systemConfig = useSystemConfig();

  const getConfirmedAppointmentRequest = useCallback(async () => {
    try {
      const {
        data: systemConfigData,
        status: systemConfigStatus,
        error: systemConfigError,
      } = systemConfig;

      if (!id) {
        throw new Error("Appointment is not found");
      }

      setIsLoading(true);

      if (systemConfigStatus === "loading" || !supabase) {
        return;
      }

      if (systemConfigStatus === "error") {
        throw new Error(systemConfigError);
      }

      const { data: appointment, error: appointmentError } = await supabase
        .from("appointments")
        .select(supabaseConfirmedAppointmentRequest)
        .eq("id", id)
        .is("appointment_offerings.appointment_package_id", null)
        .limit(1, { foreignTable: "center.center_configs" })
        .limit(1, { foreignTable: "appointment_customer_details" })
        .limit(1, { foreignTable: "appointment_patient_details" })
        .single<SupabaseConfirmedAppointment>();

      if (appointmentError) {
        throw new Error(appointmentError.message);
      }

      if (appointment) {
        const parsedAppointment = parseConfirmedAppointment(
          appointment,
          systemConfigData,
        );

        setData(parsedAppointment);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [id, supabase, systemConfig]);

  useEffect(() => {
    const fetchAndCheck = async () => {
      await getConfirmedAppointmentRequest();
      if (data?.status === "confirmed" && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    fetchAndCheck();

    intervalRef.current = setInterval(fetchAndCheck, refetchIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [getConfirmedAppointmentRequest, data?.status]);

  return { error, isLoading, data, refetch: getConfirmedAppointmentRequest };
};

export default useConfirmedAppointment;
